/**
 * usePlanUsage — Hook to fetch and cache the user's plan usage and limits.
 */

import { useState, useEffect, useCallback } from 'react';
import { planUsageAPI } from '../services/api';

export interface UsageStat {
  current: number;
  limit: number; // -1 = unlimited
}

export interface PlanUsage {
  plan: {
    id: string;
    name: string;
  };
  usage: {
    items: UsageStat;
    categories: UsageStat;
    workspaces: UsageStat;
    aiChatMessages: UsageStat;
    members: { limit: number };
  };
  features: {
    aiCategorization: boolean;
    aiValuePrediction: boolean;
    aiDuplicateDetection: boolean;
    maxImagesPerItem: number;
    storageGB: number;
    exportData: boolean;
    comments: boolean;
    realTimeSync: boolean;
    apiAccess: boolean;
  };
}

export function usePlanUsage() {
  const [planUsage, setPlanUsage] = useState<PlanUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUsage = useCallback(async () => {
    try {
      setLoading(true);
      const response = await planUsageAPI.getUsage();
      setPlanUsage(response.data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch plan usage');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  const isAtLimit = useCallback((resource: 'items' | 'categories' | 'workspaces' | 'aiChatMessages'): boolean => {
    if (!planUsage) return false;
    const stat = planUsage.usage[resource];
    if (!stat || !('current' in stat)) return false;
    if (stat.limit === -1) return false;
    return stat.current >= stat.limit;
  }, [planUsage]);

  const getUsagePercent = useCallback((resource: 'items' | 'categories' | 'workspaces' | 'aiChatMessages'): number => {
    if (!planUsage) return 0;
    const stat = planUsage.usage[resource];
    if (!stat || !('current' in stat)) return 0;
    if (stat.limit === -1) return 0;
    if (stat.limit === 0) return 100;
    return Math.min(100, Math.round((stat.current / stat.limit) * 100));
  }, [planUsage]);

  const formatLimit = useCallback((limit: number): string => {
    if (limit === -1) return 'Unlimited';
    return limit.toLocaleString();
  }, []);

  return {
    planUsage,
    loading,
    error,
    refetch: fetchUsage,
    isAtLimit,
    getUsagePercent,
    formatLimit,
  };
}
